import { Box, Button } from '@mui/material';
import { AppTable, BodyText, InitialsAvatar, SubTitleText, TabButton, TitleText } from 'Application/components';
import { FC, useState } from 'react';
import MainCard from 'ui-component/cards/MainCard';
import { DisplayedPremium, TravelPlanBenefitsTab } from 'Application/models';
import { AddBoxOutlined } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import React from 'react';
import BackTo from 'Application/components/NavigateBack';
import { useCreatePlanStore, usePremiumStore } from 'Application/store';
import SetBenefit from 'Application/components/Modals/SetBenefit';
import { PremiumTable } from 'Application/components/PremiumTable';
import SetPricings from 'Application/components/Modals/SetPricings';
import Loader from 'ui-component/Loader';

interface TravelPlanBenefitsProps {}

export const TravelPlanBenefits: FC<TravelPlanBenefitsProps> = () => {
    const [activeTab, setActiveTab] = useState<TravelPlanBenefitsTab>(TravelPlanBenefitsTab.Benefits);
    const { planID } = useParams<{ planID: string }>(); // Access planID from route parameters
    const { fetchedPlans, getSinglePlan } = useCreatePlanStore(); // Access the store
    const { getPremiumData, getPremium } = usePremiumStore(); // Access the store

    const premiumData: DisplayedPremium[] = getPremiumData.map((premium) => {
        return {
            ...premium,
            maxDays: premium.maxDays === null ? '---' : premium.maxDays,
            minDays: premium.minDays === null ? '---' : premium.minDays
        };
    });

    const planNameInitial = fetchedPlans.name ? fetchedPlans.name.split('')[0].toUpperCase() : '';

    const [isSetBenefitOpen, setIsBenefitDialogOpen] = useState(false);
    const [isSetPricingsOpen, setIsPricingsDialogOpen] = useState(false);
    const [isLoadingPlans, setIsLoadingPlans] = useState(true);
    const toggleSelectedItem = (value: TravelPlanBenefitsTab) => {
        setActiveTab(value);
    };

    const setBenefitDialog = () => {
        setIsBenefitDialogOpen(true);
    };

    const setPricingsDialog = () => {
        setIsPricingsDialogOpen(true);
    };

    React.useEffect(() => {
        if (planID) {
            setIsLoadingPlans(true);
            Promise.all([getSinglePlan(planID), getPremium(planID)]).then(() => {
                setIsLoadingPlans(false);
            });
        }
    }, [planID, getSinglePlan, getPremium]);

    return (
        <>
            <BackTo title={'Back To Plans'} />

            <MainCard>
                <Box className="flex items-center space-x-6 mb-6">
                    <InitialsAvatar initials={planNameInitial} />
                    <TitleText text={fetchedPlans.name} />
                </Box>

                <Box className="flex items-center space-x-2 mb-6">
                    <TabButton
                        isActive={activeTab === TravelPlanBenefitsTab.Benefits}
                        text={'Benefits'}
                        onClick={() => toggleSelectedItem(TravelPlanBenefitsTab.Benefits)}
                    />
                    <TabButton
                        isActive={activeTab === TravelPlanBenefitsTab.Pricing}
                        text={'Pricing'}
                        onClick={() => toggleSelectedItem(TravelPlanBenefitsTab.Pricing)}
                    />
                </Box>

                {activeTab === TravelPlanBenefitsTab.Benefits ? (
                    <Box className="border rounded-xl shadow-sm border-slate-200">
                        <Box className="flex flex-wrap justify-between items-center p-6" id="header-section">
                            <Box className="flex flex-col space-y-2" id="title-section">
                                <SubTitleText text={'Plan Benefits'} />
                                <BodyText text={'Manage plan benefits'} />
                            </Box>
                            <Button
                                onClick={setBenefitDialog}
                                className="text-sky-700 border-sky-200 bg-sky-50 rounded-md mt-2 sm:mt-0"
                                variant="outlined"
                                startIcon={<AddBoxOutlined />}
                            >
                                Set Benefit
                            </Button>
                        </Box>
                        {isLoadingPlans ? <Loader /> : <AppTable data={fetchedPlans.travelBenefits} hasCheckboxSelection={true} />}
                    </Box>
                ) : (
                    <Box className="border rounded-xl shadow-sm border-slate-200">
                        <Box className="flex flex-wrap justify-between items-center p-6" id="header-section">
                            <Box className="flex flex-col space-y-2" id="title-section">
                                <SubTitleText text={'Plan Pricing'} />
                                <BodyText text={'Manage plan pricing'} />
                            </Box>
                            <Button
                                onClick={setPricingsDialog}
                                className="text-sky-700 border-sky-200 bg-sky-50 rounded-md mt-2 sm:mt-0"
                                variant="outlined"
                                startIcon={<AddBoxOutlined />}
                            >
                                Set Pricings
                            </Button>
                        </Box>
                        <PremiumTable data={premiumData} hasCheckboxSelection currency={fetchedPlans.currencyType} />
                    </Box>
                )}
            </MainCard>
            <SetBenefit isOpen={isSetBenefitOpen} setIsOpen={setIsBenefitDialogOpen} />
            <SetPricings isOpen={isSetPricingsOpen} setIsOpen={setIsPricingsDialogOpen} />
        </>
    );
};
